import axios from "axios";
import type { AppThunk } from "@Store/index";
import {
  updateNewsHomeData,
  updateNewsHomeReadyStatus,
  updateNewsHomeError,
  type NewsPostSimple,
  type NewsCategory,
} from "./newsHomeReducer";
import { extractTextFromPostBody, formatNewsDate } from "../services/getHomeFeaturedNews";
import type { HeroNews } from "../constants";
import config from "@Config/index";
import { getMetaSuccess } from "@App/appReducer";

interface GetNewsHomeDataParams {
  staticContext?: any;
}

const getNewsHomeData =
  ({ staticContext }: GetNewsHomeDataParams = {}): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateNewsHomeReadyStatus("request"));

      const [
        featuredResponse,
        latestResponse,
        categoriesResponse,
        sidebarFeaturedResponse,
        amberReportsResponse,
        amberBeatsResponse,
      ] = await Promise.all([
        axios.get(`${config.CMS_API_URL}/api/news-home-featured-limited`),
        axios.get(
          `${config.CMS_API_URL}/api/news-posts?limit=12&sort=-date&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[postType]=true&where[_status][equals]=published&where[isArchived][equals]=false`,
        ),
        axios.get(
          `${config.CMS_API_URL}/api/news-categories?select[id]=true&select[name]=true&select[slug]=true&limit=0&where[_status][equals]=published`,
        ),
        axios.get(
          `${config.CMS_API_URL}/api/news-posts?where[featured][equals]=true&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[postType]=true&limit=3&sort=-date&where[_status][equals]=published&where[isArchived][equals]=false`,
        ),
        axios.get(
          `${config.CMS_API_URL}/api/news-posts?where[postType][equals]=amber-report&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[postType]=true&limit=10&sort=-date&where[_status][equals]=published&where[isArchived][equals]=false`,
        ),
        axios.get(
          `${config.CMS_API_URL}/api/news-posts?where[postType][equals]=amber-beat&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[postType]=true&limit=10&sort=-date&where[_status][equals]=published&where[isArchived][equals]=false`,
        ),
      ]);

      // Transform featured news
      const homeFeaturedNews: HeroNews[] = (featuredResponse.data?.data || []).map((post: any) => ({
        id: post.id.toString(),
        title: post.name,
        category: post.category?.name || "",
        readTime: post.readTime || "5 min read",
        publishDate: formatNewsDate(post.date || post.updatedAt),
        description:
          post.postBody?.root?.children && post.postBody.root.children.length > 0
            ? extractTextFromPostBody(
                { root: { type: post.postBody.root.type, children: post.postBody.root.children } },
                75,
              )
            : "",
        image: post.mainImage?.url || "",
        slug: post.slug,
        postType: post.postType || "news",
      }));

      // Transform latest news
      const latestNews: NewsPostSimple[] = (latestResponse.data?.docs || []).map((post: any) => ({
        id: post.id,
        name: post.name,
        slug: post.slug,
        postType: post.postType || "news",
        mainImage: post.mainImage,
        category: post.category,
        date: post.date,
        updatedAt: post.updatedAt,
        readTime: post.readTime,
      }));

      // Transform sidebar featured news
      const sidebarFeaturedNews: NewsPostSimple[] = (sidebarFeaturedResponse.data?.docs || []).map(
        (post: any) => ({
          id: post.id,
          name: post.name,
          slug: post.slug,
          postType: post.postType || "news",
          mainImage: post.mainImage,
          category: post.category,
          date: post.date,
          updatedAt: post.updatedAt,
          readTime: post.readTime,
        }),
      );

      // Transform amber reports
      const amberReports: NewsPostSimple[] = (amberReportsResponse.data?.docs || []).map(
        (post: any) => ({
          id: post.id,
          name: post.name,
          slug: post.slug,
          postType: post.postType || "amber-report",
          mainImage: post.mainImage,
          category: post.category,
          date: post.date,
          updatedAt: post.updatedAt,
          readTime: post.readTime,
        }),
      );

      // Transform amber beats
      const amberBeats: NewsPostSimple[] = (amberBeatsResponse.data?.docs || []).map(
        (post: any) => ({
          id: post.id,
          name: post.name,
          slug: post.slug,
          postType: post.postType || "amber-beat",
          mainImage: post.mainImage,
          category: post.category,
          date: post.date,
          updatedAt: post.updatedAt,
          readTime: post.readTime,
        }),
      );

      const totalDocs = latestResponse.data?.totalDocs || 0;
      const categories: NewsCategory[] = categoriesResponse.data?.docs || [];

      if (homeFeaturedNews.length === 0 && latestNews.length === 0) {
        console.error("No news data found");
        dispatch(updateNewsHomeReadyStatus("failure"));
        if (staticContext) {
          staticContext.statusCode = 404;
        }
        return;
      }

      const meta: any = {};
      meta.title = "Amber News - Student Housing News, Updates & Insights";
      meta.description =
        "Stay updated with the latest student housing news, market updates, and insights from Amber.";
      meta.keywords = ["student housing news", "student accommodation", "housing updates"];
      meta.excludeAlternateLinks = true;

      dispatch(getMetaSuccess(meta));
      dispatch(
        updateNewsHomeData({
          homeFeaturedNews,
          sidebarFeaturedNews,
          latestNews,
          categories,
          totalDocs,
          amberReports,
          amberBeats,
        }),
      );
      dispatch(updateNewsHomeReadyStatus("success"));
    } catch (error) {
      console.error("Error fetching news home data:", error);
      dispatch(updateNewsHomeError(error));
      dispatch(updateNewsHomeReadyStatus("failure"));
      if (staticContext) {
        staticContext.statusCode = 500;
      }
    }
  };

export default getNewsHomeData;
